import React from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import {
  ArrowRight, AlertCircle, Sparkles, Check, TrendingUp, Megaphone,
  Coffee, ShoppingBag, Scissors, Wrench, HeartPulse, GraduationCap, Car,
  Building2, Store, Crosshair, Footprints, Briefcase, Package, Home, Plane
} from "lucide-react";
import { findIndustry, INDUSTRIES } from "../data/industries";
import { FootTrafficAuditForm } from "../components/FootTrafficAuditForm";
import { SEO } from "../components/SEO";
import { IndustryTestimonial } from "../components/IndustryTestimonial";

// Map iconName strings to lucide components
const ICONS = {
  Coffee,
  ShoppingBag,
  Megaphone,
  Scissors,
  Wrench,
  HeartPulse,
  GraduationCap,
  Car,
  Building2,
  Store,
  Briefcase,
  Package,
  Home,
  Plane
};

const ChallengeCard = ({ c }) => (
  <div className="bg-gradient-to-br from-red-50 to-white p-6 rounded-2xl border border-red-100">
    <AlertCircle className="text-red-500 mb-3" size={32} />
    <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{c.title}</h3>
    <p className="text-gray-medium text-sm">{c.desc}</p>
  </div>
);

const StrategyCard = ({ s }) => (
  <div className="bg-white p-6 rounded-2xl shadow-card card-hover border border-gray-100">
    <div className="w-12 h-12 rounded-xl bg-orange-light flex items-center justify-center mb-4">
      <Crosshair className="text-orange-primary" size={22} />
    </div>
    <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{s.title}</h3>
    <p className="text-gray-medium text-sm">{s.desc}</p>
  </div>
);

const UseCaseCard = ({ u }) => (
  <div className="bg-gradient-to-br from-orange-light to-white p-7 rounded-2xl border border-orange-primary/10 card-hover">
    <Megaphone className="text-orange-primary mb-3" size={32} />
    <h3 className="font-display font-bold text-xl text-gray-dark mb-2">{u.title}</h3>
    <p className="text-gray-medium mb-4">{u.desc}</p>
    <div className="space-y-2 pt-4 border-t border-orange-primary/10">
      {u.bullets.map((b) => (
        <div key={b} className="flex items-center gap-2">
          <Check size={16} className="text-orange-primary flex-shrink-0" />
          <span className="text-sm text-gray-dark">{b}</span>
        </div>
      ))}
    </div>
  </div>
);

// Suggest other industries at the bottom
const RelatedIndustries = ({ currentSlug }) => {
  const others = INDUSTRIES.filter((i) => i.slug !== currentSlug).slice(0, 4);
  return (
    <section className="section-padding bg-gray-light" data-testid="related-industries-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-dark mb-3">
            Explore Other Industries
          </h2>
          <p className="text-gray-medium">
            Different vertical, same outcome engine. See how SmartReach&trade; works for:
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {others.map((ind) => {
            const Icon = ICONS[ind.iconName] || Store;
            return (
              <Link
                key={ind.slug}
                to={`/industries/${ind.slug}`}
                className="bg-white p-5 rounded-2xl shadow-card border border-gray-100 card-hover text-center block"
                data-testid={`related-industry-${ind.slug}`}
              >
                <Icon className="text-orange-primary mx-auto mb-2" size={28} />
                <p className="font-display font-semibold text-gray-dark text-sm">{ind.name}</p>
              </Link>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <Link to="/industries" className="text-orange-primary font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
            View all industries <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const IndustryDetailPage = () => {
  const { slug } = useParams();
  const industry = findIndustry(slug);
  const navigate = useNavigate();

  if (!industry) {
    return <Navigate to="/industries" replace />;
  }

  const Icon = ICONS[industry.iconName] || Store;

  // Service JSON-LD — per-industry SEO discoverability
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": `SmartReach™ for ${industry.name}`,
    "name": `SmartReach™ for ${industry.name}`,
    "description": industry.intro,
    "provider": {
      "@type": "Organization",
      "name": "FunAsia Digital",
      "url": "https://funasiadigital.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": industry.name
    },
    "url": `https://funasiadigital.com/industries/${industry.slug}`
  };

  return (
    <div className="pt-20" data-testid={`industry-detail-${industry.slug}`}>
      <SEO
        title={`SmartReach™ for ${industry.name}`}
        description={industry.intro.slice(0, 200)}
        path={`/industries/${industry.slug}`}
      >
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      </SEO>

      {/* HERO */}
      <section className={`section-padding bg-gradient-to-br ${industry.accent}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center text-white">
            <div>
              <Link to="/industries" className="inline-flex items-center gap-2 text-white/80 text-sm mb-4 hover:text-white">
                ← All Industries
              </Link>
              <span className="inline-flex items-center gap-2 font-semibold text-sm mb-4 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full">
                <Icon size={16} /> {industry.name}
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                {industry.tagline}
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-8">{industry.intro}</p>
              <button
                onClick={() => navigate("/contact")}
                className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                data-testid="industry-hero-cta"
              >
                Get Started <ArrowRight size={20} className="inline ml-2" />
              </button>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="aspect-square w-full max-w-md bg-white/15 backdrop-blur-sm rounded-3xl flex items-center justify-center relative overflow-hidden border border-white/20">
                <Icon size={180} strokeWidth={1.2} className="text-white opacity-90" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The <span className="text-gradient-orange">Challenges</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Where {industry.name.toLowerCase()} growth typically gets stuck.
            </p>
          </div>
          <div className={`grid grid-cols-1 ${industry.challenges.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6 max-w-5xl mx-auto`}>
            {industry.challenges.map((c) => (
              <ChallengeCard key={c.title} c={c} />
            ))}
          </div>
        </div>
      </section>

      {/* SMARTREACH STRATEGY */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-white px-3 py-1 rounded-full shadow-sm">
              <Sparkles size={14} /> SmartReach™ Strategy
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              How SmartReach&trade; Solves It For{" "}
              <span className="text-gradient-orange">{industry.name}</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              The channels, targeting, and offers that move customers in this category.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industry.strategy.map((s) => (
              <StrategyCard key={s.title} s={s} />
            ))}
          </div>
        </div>
      </section>

      {/* OPTIONAL: industry-specific testimonial (only renders if admin tagged one) */}
      <IndustryTestimonial industrySlug={industry.slug} />

      {/* USE CASES */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Use Cases</h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Concrete campaign blueprints for {industry.name.toLowerCase()}.
            </p>
          </div>
          <div className={`grid grid-cols-1 ${industry.useCases.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6`}>
            {industry.useCases.map((u) => (
              <UseCaseCard key={u.title} u={u} />
            ))}
          </div>
        </div>
      </section>

      {/* KPIs / RESULTS */}
      <section className="section-padding bg-gradient-to-br from-gray-dark via-gray-900 to-gray-dark text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
              The <span className="text-orange-secondary">Outcomes That Matter</span>
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto text-lg">
              Typical results customers see from a SmartReach&trade; {industry.name.toLowerCase()} campaign.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {industry.kpis.map((k) => (
              <div key={k.label} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10" data-testid={`kpi-${k.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <TrendingUp className="text-orange-secondary mx-auto mb-3" size={32} />
                <p className="text-4xl md:text-5xl font-display font-bold text-orange-secondary mb-2">{k.val}</p>
                <p className="text-sm text-gray-300">{k.label}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 mt-6 italic">
            * Illustrative ranges based on benchmark accounts. Your results will be unique to your business.
          </p>
        </div>
      </section>

      {/* AUDIT FORM */}
      <FootTrafficAuditForm
        title={`Get a custom ${industry.name} growth plan`}
        subtitle={`Tell us about your business. We'll send back a SmartReach™ plan tailored for ${industry.name.toLowerCase()} in your area.`}
        variant="light"
      />

      {/* RELATED */}
      <RelatedIndustries currentSlug={industry.slug} />

      {/* FINAL CTA */}
      <section className="section-padding gradient-orange">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to grow your {industry.name.toLowerCase()} business?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Let SmartReach&trade; turn local audiences into walk-in customers.
          </p>
          <button
            onClick={() => navigate("/contact")}
            className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            data-testid="industry-final-cta"
          >
            <Footprints size={20} className="inline mr-2" /> Drive Real Customers
          </button>
        </div>
      </section>
    </div>
  );
};

export default IndustryDetailPage;
